import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { post } from "../lib/api";
import { Seo } from "../lib/seo";
import { Section, SectionHead } from "../components/ui";
import { DOCS_URL } from "../data/nav";

const TYPES = ["Sales", "Support", "Billing", "Security", "Press", "Partnerships"];

export default function Contact() {
  const [sp] = useSearchParams();
  const [form, setForm] = useState({ name: "", work_email: "", company: "", country: "", enquiry_type: "Sales", message: "", consent: false, website: "" });
  const [state, setState] = useState({ loading: false, ok: false, error: "" });
  useEffect(() => { if (sp.get("intent") === "sales") setForm((f) => ({ ...f, enquiry_type: "Sales" })); }, [sp]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, error: "" });
    try { const r = await post("/leads", { ...form, source_page: "/contact/" }); setState({ loading: false, ok: true, error: "", message: r.message }); }
    catch (err) { setState({ loading: false, ok: false, error: err?.response?.data?.detail || "Something went wrong. Please try again." }); }
  };
  return (
    <>
      <Seo title="Contact" path="/contact/" description="Talk to BloomCONNECT — or talk to the AI, which handles most of it. Sales, support, billing, security and partnerships."
        jsonLd={{ "@context": "https://schema.org", "@type": "Organization", name: "BloomCONNECT", legalName: "Bloom DevLabs Corp.", contactPoint: { "@type": "ContactPoint", email: "connect.support@bybloomlabs.com", contactType: "customer support" } }} />
      <Section>
        <SectionHead label="Contact" title="Talk to us. Or talk to the AI — it handles most of it." />
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {[["Chat with the AI", "Instant. Handles ~90% of questions.", "/demo/"], ["Email", "connect.support@bybloomlabs.com · reply within one business day", "mailto:connect.support@bybloomlabs.com"], ["Help Centre", "Guides, setup and API docs", DOCS_URL]].map(([t, d, h]) => (
            <a key={t} href={h} className="card-hairline p-5 hover:border-brand-600"><div className="text-lg text-ink-950">{t}</div><div className="mt-1 text-sm text-ink-400">{d}</div></a>
          ))}
        </div>
      </Section>
      <Section sunk>
        <div className="grid gap-10 lg:grid-cols-[1.4fr,1fr] items-start">
          <div>
            {state.ok ? (
              <div className="card-hairline p-8"><h2 className="text-2xl text-ink-950">Thanks.</h2><p className="mt-2 text-ink-600">{state.message || "We'll be in touch within one business day."}</p></div>
            ) : (
              <form onSubmit={submit} className="space-y-4" noValidate>
                <input type="text" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Name" required><input required value={form.name} onChange={set("name")} className="field" /></Field>
                  <Field label="Work email" required><input required type="email" value={form.work_email} onChange={set("work_email")} className="field" /></Field>
                  <Field label="Company"><input value={form.company} onChange={set("company")} className="field" /></Field>
                  <Field label="Country"><input value={form.country} onChange={set("country")} className="field" /></Field>
                </div>
                <Field label="Enquiry type"><select value={form.enquiry_type} onChange={set("enquiry_type")} className="field">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
                <Field label="Message" required><textarea required rows={5} value={form.message} onChange={set("message")} className="field" /></Field>
                <label className="flex items-start gap-2 text-sm text-ink-600"><input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1" /><span>I agree to be contacted about my enquiry and accept the privacy policy.</span></label>
                {state.error && <p className="text-sm text-negative" role="alert">{state.error}</p>}
                <button type="submit" disabled={state.loading} className="btn-primary btn-lg">{state.loading ? "Sending…" : "Send message"}</button>
              </form>
            )}
          </div>
          <aside>
            <div className="eyebrow mb-3">Who you are reaching</div>
            <dl className="divide-y divide-line border-y border-line">
              {[["Legal name", "Bloom DevLabs Corp."], ["Country", "Philippines"], ["VAT", "010-990-645-00000"], ["Email", "connect.support@bybloomlabs.com"]].map(([k, v]) => (
                <div key={k} className="flex justify-between py-3 gap-4"><dt className="text-ink-600 text-sm">{k}</dt><dd className="text-ink-800 text-sm text-right">{v}</dd></div>
              ))}
            </dl>
            <p className="mt-6 text-sm text-ink-600">AI replies in seconds. Human escalation within hours on business days. Email under 24 hours. Enterprise customers get priority support and a named contact.</p>
          </aside>
        </div>
      </Section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-1.5">{label}{required && <span className="text-signal"> *</span>}</span>
      {children}
    </label>
  );
}
